import React, { useCallback, useEffect, useRef, useState } from 'react';
import './Mobile.css';

// ═══════════════════════════════════════════════════════════════════════════
// Socle des pages en version téléphone (2026-10-24).
//
// Les six pages de relation client (Leads web, Demandes clients, Avis Google,
// Satisfaction, Litiges, Réception VIP) ont la même forme sur téléphone :
//   - une FILE de cartes, avec des puces de file (« À répondre », « Urgentes »,
//     « En retard ») et leur compteur, triée par ce qui presse ;
//   - une FICHE plein écran en une colonne, flèches ‹ › vers l'élément suivant
//     de la file, et UNE action principale collée en bas ;
//   - une FEUILLE qui monte du bas pour les choix (statuts, motifs, canal) et
//     les confirmations, à la place des listes déroulantes et des confirm().
// Même dessin que le Planning mobile (planning/PlanningMobile.js), qui reste
// autonome. Rien ne se ferme d'un geste involontaire : ni le voile de la
// feuille, ni un clic hors de la fiche (règle CRM des modales).
// ═══════════════════════════════════════════════════════════════════════════

export const REQUETE_TELEPHONE = '(max-width: 767px), (pointer: coarse) and (max-height: 500px)';

/** Vrai sur un téléphone (< 768 px, ou paysage tactile de moins de 500 px de haut). */
export function useEstTelephone() {
  const [telephone, setTelephone] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(REQUETE_TELEPHONE).matches
  );
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(REQUETE_TELEPHONE);
    const suivre = (e) => setTelephone(e.matches);
    // Safari iOS ≤ 13 : MediaQueryList n'a que addListener.
    if (mq.addEventListener) mq.addEventListener('change', suivre); else mq.addListener(suivre);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', suivre); else mq.removeListener(suivre); };
  }, []);
  return telephone;
}

const lireForce = (cle) => {
  try { return localStorage.getItem(cle) === '1'; } catch (e) { return false; }
};

/**
 * Aiguillage d'une page selon l'écran. `mobile` reçoit les props de la page
 * plus `onVersionOrdinateur`. « Version ordinateur » est un choix PAR APPAREIL
 * et PAR PAGE (localStorage `<cle>-version-ordinateur`) ; un bouton flottant
 * rend la version téléphone.
 */
export function SelonEcran({ cle, mobile: Mobile, ordinateur: Ordinateur, props }) {
  const telephone = useEstTelephone();
  const cleForce = `${cle}-version-ordinateur`;
  const [forceOrdinateur, setForceOrdinateur] = useState(() => lireForce(cleForce));

  const versionOrdinateur = useCallback(() => {
    try { localStorage.setItem(cleForce, '1'); } catch (e) { /* la bascule vaut pour cette visite */ }
    setForceOrdinateur(true);
  }, [cleForce]);
  const versionTelephone = useCallback(() => {
    try { localStorage.removeItem(cleForce); } catch (e) { /* idem */ }
    setForceOrdinateur(false);
  }, [cleForce]);

  const versionMobile = telephone && !forceOrdinateur;
  // Le bouton d'en-tête posé par la page ordinateur (« + Nouveau… ») ne doit
  // pas survivre au passage en version téléphone.
  const poserAction = props?.setHeaderAction;
  useEffect(() => { if (versionMobile && poserAction) poserAction(null); }, [versionMobile, poserAction]);

  if (versionMobile) return <Mobile {...props} onVersionOrdinateur={versionOrdinateur} />;
  return (
    <>
      <Ordinateur {...props} />
      {telephone && (
        <button type="button" className="mb-retour-telephone" onClick={versionTelephone}>Version téléphone</button>
      )}
    </>
  );
}

// ── Petits utilitaires ──────────────────────────────────────────────────────

export const normaliser = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

/** « il y a 25 min », « il y a 3 h », « il y a 4 j », « 12/09 ». */
export function depuis(iso, maintenant = Date.now()) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const min = Math.round((maintenant - t) / 60000);
  if (min < 1) return 'à l’instant';
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const j = Math.floor(h / 24);
  if (j < 60) return `il y a ${j} j`;
  const d = new Date(t);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getFullYear()).slice(2)}`;
}

/** « 14:32 » aujourd'hui, sinon « 21/10 14:32 ». */
export function quand(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const h = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  if (d.toDateString() === new Date().toDateString()) return h;
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ${h}`;
}

/** « 21/10/2026 » depuis une date ISO ou « AAAA-MM-JJ » (sans décalage de fuseau). */
export function dateFr(v) {
  if (!v) return '';
  const s = String(v);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR');
}

/** Numéro composable pour un lien tel: / sms:. */
export const numeroTel = (t) => String(t || '').replace(/[^\d+]/g, '');

export const messageErreur = (e, defaut = 'Erreur serveur') => e?.response?.data?.error || e?.message || defaut;

// ── Éléments ────────────────────────────────────────────────────────────────

export function Plaque({ immat }) {
  if (!immat) return null;
  return <span className="mb-plaque">{immat}</span>;
}

export function Statut({ libelle, couleur }) {
  return <span className="mb-statut" style={{ '--mb-c': couleur || 'var(--border-strong)' }}>{libelle}</span>;
}

export function Pill({ ton, children }) {
  return <span className={`mb-pill${ton ? ` mb-pill--${ton}` : ''}`}>{children}</span>;
}

const IC_TEL = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" /></svg>;
const IC_SMS = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>;
const IC_MAIL = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 6-10 7L2 6" /></svg>;
const IC_LOUPE = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.5" y2="16.5" /></svg>;

/**
 * Boutons de contact. `tel` / `email` absents : bouton en pointillés, inactif,
 * avec l'explication en clair (`aria-label`). `onSms` : bouton SMS qui ouvre
 * l'écran d'envoi de la page (le SMS part du CRM, pas de l'appli du téléphone).
 */
export function ActionsContact({ tel, email, mailto, onSms, onEmail, smsDesactive }) {
  const n = numeroTel(tel);
  return (
    <div className="mb-actions">
      {n
        ? <a className="mb-action" href={`tel:${n}`}>{IC_TEL}Appeler</a>
        : <span className="mb-action" aria-disabled="true" aria-label="Aucun téléphone connu">{IC_TEL}Appeler</span>}
      {onSms && (
        <button type="button" className="mb-action" onClick={onSms} disabled={!n || !!smsDesactive} title={smsDesactive || undefined}>{IC_SMS}SMS</button>
      )}
      {onEmail
        ? <button type="button" className="mb-action" onClick={onEmail} disabled={!email}>{IC_MAIL}E-mail</button>
        : email !== undefined && (email
          ? <a className="mb-action" href={mailto || `mailto:${email}`}>{IC_MAIL}E-mail</a>
          : <span className="mb-action" aria-disabled="true" aria-label="Aucun e-mail connu">{IC_MAIL}E-mail</span>)}
    </div>
  );
}

/** Champ de recherche, avec délai de frappe (`delai` ms) avant `onChange`. */
export function Recherche({ valeur, onChange, placeholder, delai = 0, id }) {
  const [texte, setTexte] = useState(valeur || '');
  const minuteur = useRef(null);
  useEffect(() => { setTexte(valeur || ''); }, [valeur]);
  useEffect(() => () => clearTimeout(minuteur.current), []);
  const changer = (v) => {
    setTexte(v);
    clearTimeout(minuteur.current);
    if (delai > 0) minuteur.current = setTimeout(() => onChange(v), delai);
    else onChange(v);
  };
  return (
    <label className="mb-recherche" htmlFor={id}>
      {IC_LOUPE}
      <input id={id} type="search" value={texte} onChange={(e) => changer(e.target.value)}
        placeholder={placeholder} autoComplete="off" enterKeyHint="search" />
      {texte && <button type="button" className="mb-recherche-effacer" onClick={() => changer('')} aria-label="Effacer">✕</button>}
    </label>
  );
}

/** Sélecteur segmenté : [{ cle, libelle, compte }]. */
export function Segments({ options, valeur, onChange, label }) {
  return (
    <div className="mb-segments" role="tablist" aria-label={label}>
      {options.map((o) => (
        <button key={o.cle} type="button" role="tab" aria-selected={valeur === o.cle}
          className={`mb-segment${valeur === o.cle ? ' is-actif' : ''}`} onClick={() => onChange(o.cle)}>
          {o.libelle}{o.compte != null && <i>{o.compte}</i>}
        </button>
      ))}
    </div>
  );
}

/** Puces de file : [{ cle, libelle, compte, alerte }]. */
export function Puces({ options, valeur, onChange, label }) {
  return (
    <div className="mb-puces" role="tablist" aria-label={label}>
      {options.map((o) => (
        <button key={o.cle} type="button" role="tab" aria-selected={valeur === o.cle}
          className={`mb-puce${valeur === o.cle ? ' is-actif' : ''}${o.alerte && o.compte ? ' mb-puce--alerte' : ''}`}
          onClick={() => onChange(o.cle)}>
          {o.libelle}{o.compte != null && <span>{o.compte}</span>}
        </button>
      ))}
    </div>
  );
}

export function Chiffres({ items }) {
  return (
    <div className="mb-chiffres">
      {items.map((c) => (
        <div key={c.libelle} className={`mb-chiffre${c.alerte ? ' mb-chiffre--alerte' : ''}`}>
          <b>{c.valeur}</b><span>{c.libelle}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Liste groupée. `groupe(el)` donne l'intertitre ; une rupture de valeur ouvre
 * un nouveau groupe (la liste doit donc être triée par groupe).
 */
export function Liste({ elements, cle, groupe, carte, onOuvrir, vide, chargement, erreur }) {
  if (erreur) return <p className="mb-vide mb-vide--erreur">{erreur}</p>;
  if (chargement && !elements.length) return <p className="mb-vide">Chargement…</p>;
  if (!elements.length) return <p className="mb-vide">{vide || 'Rien dans cette file.'}</p>;
  const sortie = [];
  let courant;
  elements.forEach((el, i) => {
    const g = groupe ? groupe(el) : null;
    if (g && g !== courant) {
      courant = g;
      sortie.push(<div key={`g-${g}-${i}`} className="mb-groupe"><span>{g}</span></div>);
    }
    const c = carte(el);
    sortie.push(
      <button key={cle(el)} type="button" className={`mb-carte${c.nonLu ? ' is-non-lu' : ''}`}
        style={{ '--mb-c': c.couleur || 'transparent' }} onClick={() => onOuvrir(i)}>
        {c.contenu}
      </button>
    );
  });
  return <div className="mb-liste">{sortie}</div>;
}

/** Ligne du haut d'une carte : titre (+ sous-titre) et ancienneté. */
export function CarteLigne({ avant, titre, sous, age, retard }) {
  return (
    <div className="mb-carte-ligne">
      {avant}
      <span className="mb-carte-titre">{titre}{sous && <span className="mb-carte-sous">{sous}</span>}</span>
      {age && <span className={`mb-carte-age${retard ? ' is-retard' : ''}`}>{age}</span>}
    </div>
  );
}

export function LienOrdinateur({ onClick, children = 'Version ordinateur' }) {
  if (!onClick) return null;
  return <button type="button" className="mb-lien-ordinateur" onClick={onClick}>{children}</button>;
}

/**
 * Fiche plein écran. `position` / `total` affichent « 3/18 » ; `onPrecedent`
 * / `onSuivant` passent à l'élément voisin de la file. `pied` : boutons du bas.
 */
export function Fiche({ titre, position, total, onRetour, retour = '‹ Liste', onPrecedent, onSuivant, pied, children, label }) {
  const defile = useRef(null);
  useEffect(() => { if (defile.current) defile.current.scrollTop = 0; }, [position]);
  // Le corps de la page ne défile plus sous la fiche.
  useEffect(() => {
    const avant = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = avant; };
  }, []);
  return (
    <div className="mb-fiche" role="dialog" aria-modal="true" aria-label={label || titre}>
      <div className="mb-fiche-barre">
        <button type="button" className="mb-retour" onClick={onRetour}>{retour}</button>
        <span className="mb-fiche-titre">{titre}{total > 1 && position != null ? ` · ${position + 1}/${total}` : ''}</span>
        {(onPrecedent || onSuivant) && (
          <>
            <button type="button" className="mb-nav" onClick={onPrecedent} disabled={!onPrecedent} aria-label="Précédent">‹</button>
            <button type="button" className="mb-nav" onClick={onSuivant} disabled={!onSuivant} aria-label="Suivant">›</button>
          </>
        )}
      </div>
      <div className="mb-fiche-defile" ref={defile}>{children}</div>
      {pied && <div className="mb-fiche-pied">{pied}</div>}
    </div>
  );
}

/** Feuille montante. Ne se ferme QUE par « Fermer » ou une action explicite. */
export function Feuille({ titre, onFermer, children, fermer = 'Fermer' }) {
  return (
    <>
      <div className="mb-voile" aria-hidden="true" />
      <div className="mb-feuille" role="dialog" aria-modal="true" aria-label={titre}>
        <div className="mb-feuille-poignee" />
        <div className="mb-feuille-tete">
          <b>{titre}</b>
          {onFermer && <button type="button" onClick={onFermer}>{fermer}</button>}
        </div>
        <div className="mb-feuille-corps">{children}</div>
      </div>
    </>
  );
}

export function Bloc({ titre, compte, resume, ouvert, onOuvrir, children }) {
  return (
    <details className="mb-bloc" open={ouvert} onToggle={onOuvrir ? (e) => { if (e.currentTarget.open) onOuvrir(); } : undefined}>
      <summary>
        <span>{titre}</span>
        {compte != null && <span className="mb-bloc-compte">{compte}</span>}
        {resume && <span className="mb-bloc-resume">{resume}</span>}
      </summary>
      <div className="mb-bloc-corps">{children}</div>
    </details>
  );
}

export function Kv({ lignes }) {
  return (
    <dl className="mb-kv">
      {lignes.filter(Boolean).map(([k, v]) => (
        <React.Fragment key={k}><dt>{k}</dt><dd>{v === null || v === undefined || v === '' ? '—' : v}</dd></React.Fragment>
      ))}
    </dl>
  );
}

/** Saisie d'un commentaire sur une ligne (Entrée envoie). */
export function SaisieCommentaire({ onEnvoyer, placeholder = 'Ajouter un commentaire', id, avant }) {
  const [texte, setTexte] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const envoyer = async () => {
    const t = texte.trim();
    if (!t || envoi) return;
    setEnvoi(true); setErreur('');
    try { await onEnvoyer(t); setTexte(''); } catch (e) { setErreur(messageErreur(e, 'Commentaire non enregistré')); }
    finally { setEnvoi(false); }
  };
  return (
    <>
      <div className="mb-saisie">
        {avant}
        <input id={id} value={texte} onChange={(e) => setTexte(e.target.value)} placeholder={placeholder}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); envoyer(); } }} enterKeyHint="send" />
        <button type="button" onClick={envoyer} disabled={!texte.trim() || envoi}>{envoi ? '…' : 'Ajouter'}</button>
      </div>
      {erreur && <p className="mb-aide mb-aide--erreur">{erreur}</p>}
    </>
  );
}

/** Message bref en bas d'écran ; `afficher(texte)` le montre 2,2 s. */
export function useToast() {
  const [texte, setTexte] = useState('');
  const minuteur = useRef(null);
  useEffect(() => () => clearTimeout(minuteur.current), []);
  const afficher = useCallback((t) => {
    setTexte(t);
    clearTimeout(minuteur.current);
    minuteur.current = setTimeout(() => setTexte(''), 2200);
  }, []);
  const element = texte ? <div className="mb-toast" role="status">{texte}</div> : null;
  return [element, afficher];
}

/**
 * Navigation dans une file : index de l'élément ouvert, avec ‹ › bornés.
 * L'élément est retrouvé par sa clé si la liste change pendant que la fiche
 * est ouverte (rafraîchissement, archivage).
 */
export function useFicheOuverte(elements, cle) {
  const [cleOuverte, setCleOuverte] = useState(null);
  const index = cleOuverte == null ? -1 : elements.findIndex((e) => cle(e) === cleOuverte);
  const ouvrir = useCallback((i) => setCleOuverte(elements[i] ? cle(elements[i]) : null), [elements, cle]);
  const fermer = useCallback(() => setCleOuverte(null), []);
  return {
    ouverte: index >= 0 ? elements[index] : null,
    cleOuverte,
    index,
    ouvrir,
    fermer,
    precedent: index > 0 ? () => ouvrir(index - 1) : null,
    suivant: index >= 0 && index < elements.length - 1 ? () => ouvrir(index + 1) : null,
  };
}
